/**
 * Temporary Issue #49 runtime diagnostics (client-only).
 * Enable: ?liveTrace=1 or localStorage.setItem('fbc_live_trace', '1')
 * Dump: window.__fbcLiveTraceDump()
 * No PII — session id prefix only; coordinates redacted in previews.
 */

declare const __FBC_GIT_SHA__: string | undefined

export type LiveTraceAppendSource =
  | 'bridge:emitTranscriptSnapshot'
  | 'bridge:emitTranscriptDraft'
  | 'bridge:appendTranscriptChunk'
  | 'bridge:turnComplete'
  | 'composer:rest-stream'
  | 'composer:rest-final'
  | 'app:handleTranscriptUpdate'
  | 'unknown'

export type LiveTracePayload = {
  event: string
  sessionPrefix?: string | undefined
  eventType?: string | undefined
  runtimeOwner?: string | undefined
  route?: 'live' | 'rest' | 'voice-live' | 'none' | undefined
  suppressionActive?: boolean | undefined
  suppressionReason?: string | undefined
  appendSource?: LiveTraceAppendSource | undefined
  sendContextHasLocation?: boolean | undefined
  restTurnOwnerActive?: boolean | undefined
  textLen?: number | undefined
  textPreview?: string | undefined
  isFinal?: boolean | undefined
  detail?: string | undefined
  extra?: Record<string, string | number | boolean | null | undefined> | undefined
}

type LiveTraceEntry = LiveTracePayload & {
  ts: number
  buildId: string
}

const TRACE_BUFFER_MAX = 500
const traceBuffer: LiveTraceEntry[] = []

let traceEnabledCache: boolean | null = null
let bootLogged = false

function resolveBuildId(): string {
  try {
    if (typeof __FBC_GIT_SHA__ === 'string' && __FBC_GIT_SHA__.length > 0) {
      return __FBC_GIT_SHA__
    }
  } catch {
    /* define missing in dev without vite inject */
  }
  return 'dev'
}

export function isLiveRuntimeTraceEnabled(): boolean {
  if (traceEnabledCache !== null) return traceEnabledCache
  if (typeof window === 'undefined') {
    traceEnabledCache = false
    return false
  }
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get('liveTrace') === '1') {
      traceEnabledCache = true
      return true
    }
    if (window.localStorage.getItem('fbc_live_trace') === '1') {
      traceEnabledCache = true
      return true
    }
  } catch {
    traceEnabledCache = false
    return false
  }
  traceEnabledCache = false
  return false
}

export function sessionIdPrefix(sessionId: string | null | undefined): string | undefined {
  if (!sessionId || typeof sessionId !== 'string') return undefined
  const trimmed = sessionId.trim()
  if (!trimmed) return undefined
  return trimmed.slice(0, 8)
}

export function redactTraceText(text: string | undefined | null, maxLen = 56): string | undefined {
  if (!text || typeof text !== 'string') return undefined
  const compact = text.replace(/\s+/g, ' ').trim()
  if (!compact) return undefined
  return compact
    .slice(0, maxLen)
    .replace(/-?\d+\.\d+/g, '[coord]')
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[email]')
}

function pushEntry(entry: LiveTraceEntry): void {
  traceBuffer.push(entry)
  if (traceBuffer.length > TRACE_BUFFER_MAX) {
    traceBuffer.splice(0, traceBuffer.length - TRACE_BUFFER_MAX)
  }
}

export function liveRuntimeTrace(payload: LiveTracePayload): void {
  if (!isLiveRuntimeTraceEnabled()) return

  const buildId = resolveBuildId()
  if (!bootLogged) {
    bootLogged = true
    const boot: LiveTraceEntry = {
      ts: Date.now(),
      buildId,
      event: 'trace_boot',
      detail: 'live runtime trace enabled',
      extra: { href: typeof window !== 'undefined' ? window.location.pathname : '' },
    }
    pushEntry(boot)
    // eslint-disable-next-line no-console
    console.info('[FBC_LIVE_TRACE] boot', { buildId, bufferMax: TRACE_BUFFER_MAX })
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-extra-semi
      ;(window as Window & { __fbcLiveTraceDump?: () => LiveTraceEntry[] }).__fbcLiveTraceDump =
        () => [...traceBuffer]
    }
  }

  const redactedPreview = payload.textPreview
    ? redactTraceText(payload.textPreview)
    : undefined
  const entry: LiveTraceEntry = {
    ts: Date.now(),
    buildId,
    ...payload,
    ...(redactedPreview !== undefined ? { textPreview: redactedPreview } : {}),
  }
  pushEntry(entry)
  // eslint-disable-next-line no-console
  console.info('[FBC_LIVE_TRACE]', entry)
}

export function getLiveRuntimeTraceBuffer(): readonly LiveTraceEntry[] {
  return traceBuffer
}
